import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import Dialog from "@material-ui/core/Dialog";
import { useStoreon } from "storeon/react";
import LinearProgress from "@material-ui/core/LinearProgress";
import CloseIcon from "@material-ui/icons/Close";
import LeftIcon from "@material-ui/icons/ChevronLeft";

import { ProductsList } from "comp/productselector/ProductsList";
import { ProductDetails } from "comp/productselector/ProductDetails";
import { Categories } from "comp/productselector/Categories";

import { query } from "utils/general";

const Close = styled(CloseIcon)`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 1.4em;
  height: 1.4em;
  transition: 0.2s ease all;
  color: #666;
  z-index: 99;
  cursor: pointer;
  :hover {
    transform: scale(1.1);
    color: #444;
  }
`;

const DialogContent = styled.div({
  display: "flex",
  alignItems: "stretch",
  minWidth: "80vh",
  minHeight: "80vh",
  position: "relative",
  fontFamily: "Lato",
  "@media screen and (max-width: 480px)": {
    minWidth: 0,
    height: "100vh",
  },
});

const ShowCategoriesButton = styled.div(({ show }) => ({
  position: "absolute",
  visibility: show ? "visible" : "hidden",
  height: 70,
  width: 30,
  background: "#a8bb43",
  top: "calc(50% - 35px)",
  cursor: "pointer",
  opacity: 0.8,
  zIndex: 99,
  "> svg": {
    fontSize: 40,
    height: 70,
    color: "#fff",
    marginLeft: -5,
  },
}));

//TODO: refactore
let tempCanvasData;
let tempProduct;

const isMobile = window.innerWidth < 480;

const ProductSelector = () => {
  const [selectedId, setSelectedId] = useState(-1);
  const [category, setCategory] = useState(-1);
  const [showFilters, setShowFilters] = useState(false);

  const [selectedVariationId, setSelectedVariationId] = useState(-1);
  const {
    dispatch,
    products,
    canvas,
    isProductSelectorOpened,
    product,
    productCategories,
  } = useStoreon(
    "product",
    "products",
    "canvas",
    "isProductSelectorOpened",
    "productOptions",
    "proboCodes",
    "proboUploader",
    "productCategories"
  );

  useEffect(() => {
    if (!productCategories?.length) {
      return;
    }
    const category = query.getCategory();
    if (!category) {
      return;
    }
    const cat = productCategories.find((cat) => cat.slug === category);
    if (cat) {
      setCategory(cat?.id ?? -1);
      setShowFilters(true);
    }
  }, [productCategories]);

  useEffect(() => {
    if (product?.parentId) {
      setSelectedId(product.parentId);
      setSelectedVariationId(product.id);
      return;
    }
    if (product) {
      setSelectedId(product.id);
    } else {
      setSelectedId(-1);
    }
    setSelectedVariationId(-1);
  }, [product]);

  useEffect(() => {
    if (isProductSelectorOpened) {
      tempCanvasData = canvas?.DPE.persistent.toBase64();
      tempProduct = product;
      window.tempExistingProducts = canvas
        ?.getObjects()
        .filter((obj) => !obj.excludeFromExport);
    }
  }, [isProductSelectorOpened, canvas]);

  const selectedProduct = products.find((p) => p.id === selectedId);
  const selectedVariation = products.find((p) => p.id === selectedVariationId);
  const parentProducts = products
    .filter((p) => !p.parentId)
    .filter((p) => {
      if (category === -1) {
        return true;
      }
      if (p?.categories.includes(category)) {
        return true;
      }
      return false;
    });
  const productVariations = products?.filter(
    (child) => child.parentId === selectedId
  );

  let activeProduct = selectedProduct;
  if (productVariations?.length) {
    activeProduct = undefined;
  }

  if (selectedVariation) {
    activeProduct = selectedVariation;
  }

  const selectProduct = (productToSelect) => {
    if (!productToSelect) {
      setSelectedVariationId(-1);
      return;
    }
    dispatch("product/setProboOptions", []);
    if (productToSelect.parentId) {
      setSelectedVariationId(productToSelect.id);
    } else {
      if (selectedId === productToSelect.id) {
        setSelectedId(-1);
        setSelectedVariationId(-1);
        return;
      }
      setSelectedId(productToSelect.id);
      setSelectedVariationId(-1);

      const hasVariations = products?.filter(
        (p) => p.parentId === productToSelect.id
      );
      if (hasVariations.length) {
        return;
      }
    }

    if (!productToSelect.editorData) {
      return;
    }
    dispatch("product/set", productToSelect);
    setShowFilters(false);

    //TODO: move to presistent class
    canvas.DPE.cutZone.setCutRadius(0);
    canvas.DPE.persistent.loadFromBase64(productToSelect.editorData, () => {
      const { width, height } = productToSelect.size;
      if (width && height) {
        canvas.DPE.cutZone.updateSize(width * 1000, height * 1000);
      }
    });
  };

  return (
    <Dialog
      style={{ display: isProductSelectorOpened ? "block" : "none" }}
      transitionDuration={200}
      open={true}
      maxWidth={false}
      fullScreen={isMobile}
    >
      {!products?.length && <LinearProgress />}
      {product && (
        <Close
          onClick={() => {
            canvas.DPE.persistent.loadFromBase64(tempCanvasData);
            if (tempProduct) {
              dispatch("product/set", tempProduct);
            }
            dispatch("productSelector/close");
          }}
        />
      )}
      <DialogContent>
        <ShowCategoriesButton
          show={!isMobile && (selectedId !== -1 || !showFilters)}
          onClick={() => {
            setSelectedId(-1);
            setSelectedVariationId(-1);
            setShowFilters(true);
          }}
        >
          <LeftIcon />
        </ShowCategoriesButton>
        <Categories
          show={showFilters}
          onCategoryChange={(catId) => {
            setCategory(catId);
            if (isMobile) {
              setShowFilters(false);
            }
          }}
          categoryId={category}
        />
        <ProductsList
          show={
            (!isMobile && selectedId) ||
            (isMobile && !showFilters && selectedId === -1)
          }
          parentProducts={parentProducts}
          onProductSelected={selectProduct}
          selectedProduct={selectedProduct}
          categoryId={category}
          onCategoryCleared={() => {
            setCategory(-1);
          }}
          onFiltersToggle={() => {
            setShowFilters((s) => !s);
            setSelectedId(-1);
            setSelectedVariationId(-1);
          }}
        />
        <ProductDetails
          show={selectedId !== -1}
          {...{
            activeProduct,
            selectedProduct,
            selectProduct,
            productVariations,
            onProductUnselected: () => {
              setSelectedId(-1);
              setSelectedVariationId(-1);
              if (!isMobile) {
                setShowFilters(true);
              }
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ProductSelector;
